import { Component } from '@theme/component';

class WeClubSignup extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.setup();
  }

  updatedCallback() {
    super.updatedCallback();
    this.teardown();
    this.setup();
  }

  disconnectedCallback() {
    this.teardown();
    super.disconnectedCallback();
  }

  read(key, session = false) {
    try {
      return (session ? sessionStorage : localStorage).getItem(`weijmans-club-${key}`);
    } catch {
      return null;
    }
  }

  write(key, value, session = false) {
    try {
      (session ? sessionStorage : localStorage).setItem(`weijmans-club-${key}`, String(value));
    } catch {
      this.storageUnavailable = true;
    }
  }

  setup() {
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.popup = this.querySelector('dialog.we-club-popup');
    this.editor = this.dataset.editor === 'true';
    this.ready = false;
    this.shown = false;
    this.typing = false;
    const success = Boolean(this.querySelector('[data-club-success]'));
    const pendingAt = Number(this.read('pending-at', true));
    const recentPopup = this.read('pending-source', true) === 'popup' && Date.now() - pendingAt < 1200000;
    const popupHash = this.popup?.querySelector('form')?.id;
    const returnedToPopup = recentPopup || (popupHash && location.hash === `#${popupHash}`);

    if (success || this.dataset.subscribed === 'true') {
      this.write('subscribed-until', Date.now() + 180 * 86400000);
      this.write('pending-source', '', true);
    }

    for (const form of this.querySelectorAll('form.we-club__form')) {
      form.addEventListener('submit', () => {
        if (this.editor) return;
        this.write('pending-source', form.closest('dialog') ? 'popup' : 'footer', true);
        this.write('pending-at', Date.now(), true);
        this.typing = true;
      }, { signal });
    }

    if (!(this.popup instanceof HTMLDialogElement) || typeof this.popup.showModal !== 'function') return;

    for (const button of this.querySelectorAll('[data-club-open]')) {
      button.hidden = false;
      button.addEventListener('click', () => this.openPopup(true), { signal });
    }
    for (const button of this.querySelectorAll('[data-club-close]')) {
      button.addEventListener('click', () => this.popup.close(), { signal });
    }
    this.popup.addEventListener('close', () => {
      this.rememberDismissal();
      if (this.previousFocus instanceof HTMLElement && this.previousFocus.isConnected) this.previousFocus.focus({ preventScroll: true });
    }, { signal });
    this.popup.addEventListener('click', (event) => {
      if (event.target !== this.popup) return;
      const box = this.popup.getBoundingClientRect();
      if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) this.popup.close();
    }, { signal });

    if (returnedToPopup && (success || this.popup.querySelector('[data-club-error]'))) {
      this.write('pending-source', '', true);
      this.openPopup(true);
      return;
    }
    if (this.editor || this.isSuppressed()) return;

    document.addEventListener('focusin', (event) => {
      if (event.target instanceof Element && event.target.matches('input, textarea, select, [contenteditable="true"]')) this.typing = true;
    }, { signal });
    window.addEventListener('scroll', () => this.maybeOpen(), { passive: true, signal });
    document.addEventListener('visibilitychange', () => this.maybeOpen(), { signal });
    this.timer = window.setTimeout(() => {
      this.ready = true;
      this.maybeOpen();
    }, Math.max(5, Number(this.dataset.delay) || 12) * 1000);
  }

  isSuppressed() {
    return this.dataset.subscribed === 'true' ||
      Number(this.read('subscribed-until')) > Date.now() ||
      Number(this.read('dismissed-until')) > Date.now() ||
      this.read('shown', true) === 'true';
  }

  maybeOpen() {
    if (!this.ready || this.shown || this.typing || document.hidden || this.isSuppressed()) return;
    const range = document.documentElement.scrollHeight - window.innerHeight;
    if (window.scrollY < 160 || window.scrollY / Math.max(1, range) < .2) return;
    const count = document.querySelector('.cart-bubble__text-count');
    if (Number(this.dataset.cartCount) > 0 || Number.parseInt(count?.textContent || '0', 10) > 0) return;
    if (this.querySelector('section.we-club')?.getBoundingClientRect().top < window.innerHeight) return;
    const blockers = document.querySelectorAll('dialog[open], [role="dialog"], [aria-modal="true"], #shopify-pc__banner, header details[open], .we-header details[open]');
    if (Array.from(blockers).some(element => {
      if (element === this.popup || element.getAttribute('aria-hidden') === 'true' || element.closest('details:not([open])')) return false;
      const box = element.getBoundingClientRect();
      return box.width > 0 && box.height > 0 && box.bottom > 0 && box.right > 0 && box.top < window.innerHeight && box.left < window.innerWidth && getComputedStyle(element).visibility !== 'hidden';
    })) return;
    this.openPopup(false);
  }

  openPopup(manual) {
    if (!this.popup || this.popup.open || (!manual && this.isSuppressed())) return;
    this.previousFocus = document.activeElement;
    this.popup.showModal();
    this.shown = true;
    clearTimeout(this.timer);
    if (!this.editor) this.write('shown', 'true', true);
    this.rememberDismissal();
    this.popup.querySelector('h2')?.focus({ preventScroll: true });
  }

  rememberDismissal() {
    if (!this.editor) this.write('dismissed-until', Date.now() + (Number(this.dataset.cooldown) || 7) * 86400000);
  }

  teardown() {
    clearTimeout(this.timer);
    this.controller?.abort();
    if (this.popup?.open) this.popup.close();
  }
}

if (!customElements.get('we-club-signup')) customElements.define('we-club-signup', WeClubSignup);
